/**
 * The board surface: drag context, columns, cursors, header and side panels.
 *
 * ## How a drag works, end to end
 *
 * 1. `onDragStart` records which card is being dragged. Nothing else changes.
 * 2. `onDragOver` computes where the card *would* land and writes that to the
 *    preview context. The board re-renders with the card in its prospective
 *    position, so the gap the user sees is the gap they will get.
 * 3. `onDragEnd` commits: one `card.move` frame carrying the destination column
 *    and the ordinal index. The overlay entry is dropped and the authoritative
 *    card replaces it when the acknowledgement arrives.
 * 4. `onDragCancel` (Escape, or a drag that ends outside any droppable) clears
 *    the preview. Because nothing was written, there is nothing to undo.
 *
 * The important property is that steps 1 to 3 produce **no mutations at all**.
 * The common alternative - writing the reorder into the store on every
 * `onDragOver` - produces a mutation per pointer crossing, each needing its own
 * reconciliation, and a cancelled drag leaves a trail of operations to unwind.
 * Here a drag is pure local state until it ends, and a cancelled drag is
 * indistinguishable from one that never happened.
 *
 * ## Why the index is an ordinal
 *
 * The frame carries `position: 3`, not a fractional value. The server resolves
 * that ordinal against the column's actual contents at the moment it serialises
 * the write. A client-computed midpoint would be a guess against a column
 * snapshot that two peers could be mutating simultaneously, and two simultaneous
 * drags would both compute the same midpoint and stack. See `moveCardBodySchema`
 * in `packages/shared/src/http.ts`.
 *
 * ## Keyboard
 *
 * `KeyboardSensor` with `sortableKeyboardCoordinates` gives Space/Enter to pick
 * up, arrows to move, Space to drop, Escape to cancel. Every transition is
 * announced through `DndContext`'s `announcements`, which is the difference
 * between a board that is technically operable by keyboard and one a screen
 * reader user can actually use.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { Card, PublicUser, Role } from '@slate/shared';
import { useBoard } from '../state/BoardProvider.js';
import { typingFor } from '../state/board-reducer.js';
import type { DragPreview } from '../state/types.js';
import { isColumnDropId, columnIdFromDropId, ColumnView } from './ColumnView.js';
import { CardTileOverlay } from './CardTile.js';
import { BoardHeader } from './BoardHeader.js';
import { LiveCursors, useCursorReporter } from './LiveCursors.js';
import { CardDrawer } from './CardDrawer.js';
import { ActivityFeed } from './ActivityFeed.js';
import { Notices } from './Notices.js';
import { Icon, ghostButtonClass, inputClass, primaryButtonClass, useTick } from './ui.js';

/** Pixels of movement before a press becomes a drag. Six is enough that a click
 * with a slightly unsteady hand still opens the card, and small enough that a
 * deliberate drag starts immediately. */
const DRAG_ACTIVATION_DISTANCE_PX = 6;

export function BoardView({
  currentUserId,
  role,
  onBoardDeleted,
}: {
  currentUserId: string;
  role: Role;
  onBoardDeleted(): void;
}): ReactNode {
  const { state, view, actions } = useBoard();
  const [preview, setPreviewState] = useState<DragPreview | null>(null);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [addingColumn, setAddingColumn] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const cursorHandlers = useCursorReporter(contentRef);

  // One tick for the whole board, and only while there is something to expire.
  // A board with nobody typing and no live cursors does not re-render at all.
  const hasTransientState = useMemo(
    () => Object.values(state.typing).some((entries) => entries.length > 0),
    [state.typing],
  );
  const now = useTick(hasTransientState, 2_000);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const members = useMemo(() => {
    const map = new Map<string, PublicUser>();
    for (const member of state.members) map.set(member.user.id, member.user);
    return map;
  }, [state.members]);

  const typingByCard = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const cardId of Object.keys(state.typing)) {
      if (cardId === '*') continue;
      const entries = typingFor(state, cardId, currentUserId);
      if (entries.length > 0) map[cardId] = entries.map((entry) => entry.name);
    }
    return map;
    // `now` is a dependency on purpose: `typingFor` filters by age, so the map has
    // to be recomputed when the clock moves, or a stale indicator would persist
    // until something else happened to change.
  }, [state, currentUserId, now]);

  const titleOf = useCallback((id: string) => view.cardById.get(id)?.title ?? 'card', [view]);

  /**
   * Where a drag currently resolves to.
   *
   * `over.id` is either a card id or a column droppable id, and the two mean
   * different things: a card means "insert relative to this card", a column means
   * "append to this column". An empty column has no cards to be relative to, so
   * the column droppable is the only way to target it.
   *
   * The `below` flag comes from comparing the dragged card's translated rect
   * against the midpoint of the card it is over, which is what makes a card
   * inserted into the lower half of its neighbour land after it rather than
   * before it. Without that comparison every drop lands on the near side and a
   * downward drag feels like it stops one place short.
   */
  const resolveDrop = useCallback(
    (overId: string, below: boolean): DragPreview | null => {
      if (activeCardId === null) return null;

      if (isColumnDropId(overId)) {
        const columnId = columnIdFromDropId(overId);
        const cards = (view.cardsByColumn.get(columnId) ?? []).filter((card) => card.id !== activeCardId);
        return { cardId: activeCardId, columnId, index: cards.length };
      }

      const overCard = view.cardById.get(overId);
      if (overCard === undefined) return null;

      const cards = (view.cardsByColumn.get(overCard.columnId) ?? []).filter((card) => card.id !== activeCardId);
      const overIndex = cards.findIndex((card) => card.id === overId);
      if (overIndex === -1) return null;

      return { cardId: activeCardId, columnId: overCard.columnId, index: below ? overIndex + 1 : overIndex };
    },
    [activeCardId, view],
  );

  function handleDragStart(event: DragStartEvent): void {
    setActiveCardId(String(event.active.id));
  }

  function handleDragOver(event: DragOverEvent): void {
    const over = event.over;
    if (over === null) {
      setPreviewState(null);
      return;
    }

    const translated = event.active.rect.current.translated;
    const below = translated !== null && over.rect !== null && translated.top > over.rect.top + over.rect.height / 2;
    setPreviewState(resolveDrop(String(over.id), below));
  }

  function handleDragEnd(event: DragEndEvent): void {
    const target = preview;
    setPreviewState(null);
    setActiveCardId(null);
    if (target === null || event.over === null) return;

    const card = state.cards[target.cardId];
    if (card === undefined) return;

    // A drop that resolves to where the card already is produces no frame. The
    // server would detect the same no-op and skip the event, but not sending it
    // saves a round trip and, more importantly, keeps a stray drag from bumping
    // the card's version and invalidating a conditional write in flight
    // elsewhere.
    const currentIndex = (view.cardsByColumn.get(card.columnId) ?? []).findIndex((entry) => entry.id === card.id);
    if (card.columnId === target.columnId && currentIndex === target.index) return;

    actions.moveCard(card.id, target.columnId, target.index);
  }

  function handleDragCancel(): void {
    setPreviewState(null);
    setActiveCardId(null);
  }

  const activeCard: Card | null = activeCardId === null ? null : (view.cardById.get(activeCardId) ?? null);

  const announcements = useMemo<Announcements>(
    () => ({
      onDragStart({ active }) {
        return `Picked up ${titleOf(String(active.id))}.`;
      },
      onDragOver({ active, over }) {
        if (over === null) return `${titleOf(String(active.id))} is no longer over a column.`;
        if (isColumnDropId(String(over.id))) {
          const columnId = columnIdFromDropId(String(over.id));
          const name = view.columns.find((column) => column.id === columnId)?.name ?? 'a column';
          const count = (view.cardsByColumn.get(columnId) ?? []).length;
          return `${titleOf(String(active.id))} is over ${name}, which holds ${count} ${count === 1 ? 'card' : 'cards'}.`;
        }
        const overCard = view.cardById.get(String(over.id));
        if (overCard === undefined) return undefined;
        const columnName = view.columns.find((column) => column.id === overCard.columnId)?.name ?? 'a column';
        const index = (view.cardsByColumn.get(overCard.columnId) ?? []).findIndex((card) => card.id === overCard.id);
        return `${titleOf(String(active.id))} is over ${overCard.title}, position ${index + 1} of ${columnName}.`;
      },
      onDragEnd({ active, over }) {
        if (over === null) return `${titleOf(String(active.id))} was dropped outside a column and did not move.`;
        return `${titleOf(String(active.id))} was dropped.`;
      },
      onDragCancel({ active }) {
        return `Moving ${titleOf(String(active.id))} was cancelled.`;
      },
    }),
    [titleOf, view],
  );

  const drawerCard = openCardId === null ? null : (view.cardById.get(openCardId) ?? null);
  const otherColumns = view.columns.map((column) => ({ id: column.id, name: column.name }));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <BoardHeader
        activityOpen={activityOpen}
        onToggleActivity={() => setActivityOpen((open) => !open)}
        role={role}
        onBoardDeleted={onBoardDeleted}
      />

      <div className="flex min-h-0 flex-1">
        <div className="scroll-slim snap-column min-w-0 flex-1 overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            accessibility={{ announcements }}
          >
            <div
              ref={contentRef}
              onPointerMove={cursorHandlers.onPointerMove}
              onPointerLeave={cursorHandlers.onPointerLeave}
              className="relative flex min-h-full items-start gap-3 p-4"
            >
              {view.columns.map((column, index) => (
                <ColumnView
                  key={column.id}
                  column={column}
                  index={index}
                  columnCount={view.columns.length}
                  cards={view.cardsByColumn.get(column.id) ?? []}
                  members={members}
                  pendingCards={view.pendingCards}
                  typingByCard={typingByCard}
                  onOpenCard={setOpenCardId}
                  onAddCard={(columnId, title) => actions.createCard(columnId, title)}
                  onRenameColumn={(columnId, name) => void actions.renameColumn(columnId, name)}
                  onMoveColumn={(columnId, target) => actions.reorderColumn(columnId, target)}
                  onDeleteColumn={actions.deleteColumn}
                  otherColumns={otherColumns}
                />
              ))}

              <div className="w-[17.5rem] shrink-0">
                {addingColumn ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      const data = new FormData(event.currentTarget);
                      const name = String(data.get('name') ?? '').trim();
                      if (name !== '') actions.createColumn(name);
                      setAddingColumn(false);
                    }}
                    className="rounded-xl border border-border bg-surface p-2.5"
                  >
                    <label htmlFor="new-column-name" className="sr-only">
                      Column name
                    </label>
                    <input
                      id="new-column-name"
                      name="name"
                      autoFocus
                      placeholder="Column name"
                      className={inputClass}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setAddingColumn(false);
                      }}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button type="submit" className={primaryButtonClass}>
                        Add column
                      </button>
                      <button type="button" className={ghostButtonClass} onClick={() => setAddingColumn(false)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAddingColumn(true)}
                    className="flex w-full items-center gap-1.5 rounded-xl border border-dashed border-border px-3 py-2.5 text-sm text-ink-muted hover:border-border-strong hover:text-ink"
                  >
                    <Icon name="plus" size={14} />
                    Add a column
                  </button>
                )}
              </div>

              <LiveCursors now={now} />
            </div>

            <DragOverlay dropAnimation={{ duration: 160, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
              {activeCard === null ? null : (
                <CardTileOverlay
                  card={activeCard}
                  assignee={activeCard.assigneeId === null ? null : (members.get(activeCard.assigneeId) ?? null)}
                />
              )}
            </DragOverlay>
          </DndContext>
        </div>

        {activityOpen && <ActivityFeed onClose={() => setActivityOpen(false)} />}
      </div>

      {drawerCard !== null && (
        <CardDrawer
          card={drawerCard}
          members={members}
          currentUserId={currentUserId}
          onClose={() => setOpenCardId(null)}
        />
      )}

      <Notices />
    </div>
  );
}
